import { desktopProxyUrl } from '../utils/desktopRuntime';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'https://backend.auroraflux.net';

export interface SportItem {
  idSport: string;
  strSport: string;
  strSportThumb?: string;
  strSportIconGreen?: string;
}

export interface LeagueItem {
  idLeague: string;
  strLeague: string;
  strSport: string;
  strBadge?: string;
  strLogo?: string;
  strCountry?: string;
}

export interface TeamBadge {
  idTeam: string;
  strTeam: string;
  strTeamBadge?: string;
}

export interface LiveEvent {
  idEvent: string;
  strEvent?: string;
  strHomeTeam?: string;
  strAwayTeam?: string;
  intHomeScore?: string | null;
  intAwayScore?: string | null;
  strProgress?: string;
  strStatus?: string;
  strLeague?: string;
  idLeague?: string;
  strHomeTeamBadge?: string;
  strAwayTeamBadge?: string;
  strLeagueBadge?: string;
  strSport?: string;
  strCountry?: string;
  strChannel?: string;
  strChannelLogo?: string;
}

export interface UpcomingEvent {
  idEvent: string;
  strEvent?: string;
  strHomeTeam?: string;
  strAwayTeam?: string;
  strTime?: string;
  dateEvent?: string;
  strLeague?: string;
  idLeague?: string;
  strHomeTeamBadge?: string;
  strAwayTeamBadge?: string;
  strLeagueBadge?: string;
  strSport?: string;
  strCountry?: string;
  strChannel?: string;
  strChannelLogo?: string;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(desktopProxyUrl(`${BACKEND_URL}${path}`));
  if (!res.ok) throw new Error(`Sports API error: ${res.status}`);
  const json = await res.json() as { success: boolean; data: T };
  return json.data;
}

export interface SportsEventsMeta {
  events: LiveEvent[] | UpcomingEvent[] | null;
  apiTier?: 'free' | 'premium';
  configured?: boolean;
}

export interface SportsEventsResult<T> {
  events: T[];
  apiTier?: 'free' | 'premium';
  configured?: boolean;
}

function getBrowserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

export async function fetchSports(): Promise<SportItem[]> {
  const data = await get<{ sports: SportItem[] | null }>('/api/v1/sports/sports');
  return data.sports ?? [];
}

export async function fetchLeagues(sport: string): Promise<LeagueItem[]> {
  const data = await get<{ countrys: LeagueItem[] | null }>(`/api/v1/sports/leagues?sport=${encodeURIComponent(sport)}`);
  return data.countrys ?? [];
}

export async function fetchLiveEvents(sport: string, countryName?: string): Promise<LiveEvent[]> {
  return (await fetchLiveEventsDetailed(sport, countryName)).events;
}

export async function fetchTodayEvents(sport: string, countryName?: string): Promise<UpcomingEvent[]> {
  return (await fetchTodayEventsDetailed(sport, countryName)).events;
}

export async function fetchLiveEventsDetailed(sport: string, countryName?: string): Promise<SportsEventsResult<LiveEvent>> {
  var url = '/api/v1/sports/events/live?sport=' + encodeURIComponent(sport);
  if (countryName) url += '&country=' + encodeURIComponent(countryName);
  var timezone = getBrowserTimeZone();
  if (timezone) url += '&timezone=' + encodeURIComponent(timezone);
  const data = await get<SportsEventsMeta>(url);
  return {
    events: (data.events as LiveEvent[] | null) ?? [],
    apiTier: data.apiTier,
    configured: data.configured,
  };
}

export async function fetchTodayEventsDetailed(sport: string, countryName?: string): Promise<SportsEventsResult<UpcomingEvent>> {
  var url = '/api/v1/sports/events/today?sport=' + encodeURIComponent(sport);
  if (countryName) url += '&country=' + encodeURIComponent(countryName);
  var timezone = getBrowserTimeZone();
  if (timezone) url += '&timezone=' + encodeURIComponent(timezone);
  const data = await get<SportsEventsMeta>(url);
  return {
    events: (data.events as UpcomingEvent[] | null) ?? [],
    apiTier: data.apiTier,
    configured: data.configured,
  };
}

export async function fetchUpcomingEvents(leagueId: string): Promise<UpcomingEvent[]> {
  const data = await get<{ events: UpcomingEvent[] | null }>(`/api/v1/sports/events/upcoming?leagueId=${encodeURIComponent(leagueId)}`);
  return data.events ?? [];
}
